import { z } from "zod";

import {
  apiEnvelopeSchema,
  bootstrapSchema,
  quoteSchema,
  searchResultSchema,
  tasksSummarySchema,
  wyckoffAnalysisSchema,
  watchlistItemSchema,
  watchlistSchema,
} from "@/schemas/api/market";
import {
  newsArticleSchema,
  newsFeedSchema,
  newsMetaUpdateSchema,
  opsAlertsSchema,
  opsDashboardSchema,
  opsDataStatusSchema,
  opsDatasyncBootstrapSchema,
  opsDatasyncRunLogsSchema,
  opsDatasyncRunSchema,
  opsDatasyncRunStepSchema,
  opsDatasyncScheduleSchema,
  opsFreshnessSchema,
  opsSourceHealthSchema,
  opsSummarySchema,
  opsTimelineSchema,
  opsUpdateLogsSchema,
  quantPanelBootstrapSchema,
  quantPanelRunDetailSchema,
  quantPanelRunLogsSchema,
  quantPanelRunSchema,
  quantPanelRunSelectsSchema,
  quantSelectsSchema,
} from "@/schemas/api/workbench";

const API_BASE = "/api/v2";

async function request<T extends z.ZodTypeAny>(url: string, schema: T, init?: RequestInit): Promise<z.infer<T>> {
  const startedAt = performance.now();
  const response = await fetch(`${API_BASE}${url}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
    ...init,
  });
  const payload = await response.json();
  const parsed = apiEnvelopeSchema(schema).parse(payload);
  if (!response.ok || parsed.error) {
    throw new Error(parsed.error || `Request failed: ${response.status}`);
  }
  const endedAt = performance.now();
  window.dispatchEvent(new CustomEvent("lisaq:api-latency", { detail: Math.round(endedAt - startedAt) }));
  return parsed.data as z.infer<T>;
}

async function requestAbsolute<T extends z.ZodTypeAny>(url: string, schema: T, init?: RequestInit): Promise<z.infer<T>> {
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
    ...init,
  });
  const payload = await response.json();
  const parsed = apiEnvelopeSchema(schema).parse(payload);
  if (!response.ok || parsed.error) {
    throw new Error(parsed.error || `Request failed: ${response.status}`);
  }
  return parsed.data as z.infer<T>;
}

export const apiClient = {
  bootstrap() {
    return request("/terminal/bootstrap", bootstrapSchema);
  },
  searchSymbols(q: string) {
    return request(`/symbols/search?${new URLSearchParams({ q }).toString()}`, z.array(searchResultSchema));
  },
  getQuotes(symbols: string[], options?: { syncLive?: boolean }) {
    const params = new URLSearchParams({
      symbols: symbols.join(","),
      sync_live: options?.syncLive === false ? "0" : "1",
    });
    return request(`/market/quotes?${params.toString()}`, quoteSchema);
  },
  getBars(symbol: string, interval: string, adjust: string, options?: { limit?: number; before?: string; after?: string }) {
    const params = new URLSearchParams({
      interval,
      adjust,
      limit: String(options?.limit ?? 500),
    });
    if (options?.before) {
      params.set("before", options.before);
    }
    if (options?.after) {
      params.set("after", options.after);
    }
    return request(
      `/market/bars/${symbol}?${params.toString()}`,
      z.object({
        adjust: z.string(),
        bars: z.array(z.any()),
        has_more_before: z.boolean().optional().default(false),
        has_more_after: z.boolean().optional().default(false),
        period: z.string(),
        symbol: z.string(),
      }),
    );
  },
  getTodayBar(symbol: string, adjust: string, options?: { syncLive?: boolean }) {
    const params = new URLSearchParams({
      adjust,
      sync_live: options?.syncLive === false ? "0" : "1",
    });
    return request(
      `/market/bars/${symbol}/today?${params.toString()}`,
      z.object({
        symbol: z.string(),
        period: z.string(),
        adjust: z.string(),
        trade_date: z.string(),
        source: z.string(),
        in_trading_session: z.boolean(),
        in_trading_day: z.boolean(),
        live_sync_window: z.boolean().optional(),
        sync_live: z.boolean(),
        error: z.string().nullable().optional(),
        bar: z.any().nullable(),
      }),
    );
  },
  getFundamentals(symbol: string) {
    return request(`/market/fundamentals/${symbol}`, z.any());
  },
  getFactors(symbol: string) {
    return request(`/market/factors/${symbol}`, z.any());
  },
  getBoards(symbol: string) {
    return request(`/market/boards/${symbol}`, z.any());
  },
  listWatchlists() {
    return request("/watchlists", z.object({
      profile: z.any().nullable(),
      default_watchlist_id: z.number().nullable(),
      watchlists: z.array(watchlistSchema),
      max_lists_per_user: z.number(),
      max_items_per_list: z.number(),
    }));
  },
  createWatchlist(payload: { name: string; description?: string; sort_by?: string; sort_dir?: "asc" | "desc" }) {
    return request("/watchlists", watchlistSchema, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  updateWatchlist(
    watchlistId: number,
    payload: { name?: string; description?: string; position?: number; sort_by?: string; sort_dir?: "asc" | "desc" },
  ) {
    return request(`/watchlists/${watchlistId}`, watchlistSchema, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  deleteWatchlist(watchlistId: number) {
    return request(
      `/watchlists/${watchlistId}`,
      z.object({
        success: z.boolean(),
        deleted_watchlist_id: z.number(),
        fallback_watchlist_id: z.number().nullable().optional(),
      }),
      { method: "DELETE" },
    );
  },
  reorderWatchlists(orderedIds: number[]) {
    return request(
      "/watchlists/reorder",
      z.array(watchlistSchema),
      {
        method: "POST",
        body: JSON.stringify({ ordered_ids: orderedIds }),
      },
    );
  },
  listWatchlistItems(
    watchlistId: number,
    options?: { sortBy?: string; sortDir?: "asc" | "desc"; limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.sortBy) {
      params.set("sort_by", options.sortBy);
    }
    if (options?.sortDir) {
      params.set("sort_dir", options.sortDir);
    }
    if (options?.limit != null) {
      params.set("limit", String(options.limit));
    }
    if (options?.offset != null) {
      params.set("offset", String(options.offset));
    }
    const query = params.toString();
    const suffix = query ? `?${query}` : "";
    return request(`/watchlists/${watchlistId}/items${suffix}`, z.object({
      watchlist: z.any().nullable().optional(),
      items: z.array(watchlistItemSchema).optional().default([]),
      total: z.number().optional().default(0),
      limit: z.number().optional(),
      offset: z.number().optional(),
      has_more: z.boolean().optional(),
      sort_by: z.string().optional(),
      sort_dir: z.enum(["asc", "desc"]).optional(),
    }));
  },
  addWatchlistItem(
    watchlistId: number,
    payload: {
      symbol: string;
      instrument_type?: string;
      market?: string;
      alias?: string;
      note?: string;
      name_cache?: string;
      sort_order?: number;
      pinned?: boolean;
      row_color?: string;
    },
  ) {
    return request(`/watchlists/${watchlistId}/items`, z.any(), {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  bulkAddWatchlistItems(
    watchlistId: number,
    items: Array<{
      symbol: string;
      instrument_type?: string;
      market?: string;
      alias?: string;
      note?: string;
      name_cache?: string;
      sort_order?: number;
      pinned?: boolean;
      row_color?: string;
    }>,
  ) {
    return request(`/watchlists/${watchlistId}/items/bulk-add`, z.any(), {
      method: "POST",
      body: JSON.stringify({ items }),
    });
  },
  patchWatchlistItem(
    watchlistId: number,
    itemId: number,
    payload: Partial<{
      symbol: string;
      instrument_type: string;
      market: string;
      alias: string;
      note: string;
      name_cache: string;
      sort_order: number;
      pinned: boolean;
      row_color: string;
    }>,
  ) {
    return request(`/watchlists/${watchlistId}/items/${itemId}`, watchlistItemSchema, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  removeWatchlistItem(watchlistId: number, itemId: number) {
    return request(`/watchlists/${watchlistId}/items/${itemId}`, z.any(), {
      method: "DELETE",
    });
  },
  bulkDeleteWatchlistItems(watchlistId: number, itemIds: number[]) {
    return request(`/watchlists/${watchlistId}/items/bulk-delete`, z.any(), {
      method: "POST",
      body: JSON.stringify({ item_ids: itemIds }),
    });
  },
  getTaskSummary() {
    return request("/tasks/summary", tasksSummarySchema);
  },
  analyzeWyckoff(payload: {
    bars: Array<{
      time: string;
      open: number;
      high: number;
      low: number;
      close: number;
      volume?: number;
      vol?: number;
    }>;
  }) {
    return request("/wyckoff/analyze", wyckoffAnalysisSchema, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  getOpsDashboard(options?: { refresh?: boolean }) {
    const params = new URLSearchParams();
    if (options?.refresh) {
      params.set("refresh", "1");
    }
    const query = params.toString();
    return request(`/ops/dashboard${query ? `?${query}` : ""}`, opsDashboardSchema);
  },
  getOpsDataStatus(options?: { days?: number; includeToday?: boolean }) {
    const params = new URLSearchParams();
    if (options?.days != null) {
      params.set("days", String(options.days));
    }
    if (options?.includeToday != null) {
      params.set("include_today", options.includeToday ? "1" : "0");
    }
    const query = params.toString();
    return request(`/ops/data-status${query ? `?${query}` : ""}`, opsDataStatusSchema);
  },
  getOpsUpdateLogs(options?: { limit?: number }) {
    const params = new URLSearchParams();
    if (options?.limit != null) {
      params.set("limit", String(options.limit));
    }
    const query = params.toString();
    return request(`/ops/update-logs${query ? `?${query}` : ""}`, opsUpdateLogsSchema);
  },
  getOpsSummary(options?: { hours?: number }) {
    const params = new URLSearchParams();
    if (options?.hours != null) {
      params.set("hours", String(options.hours));
    }
    const query = params.toString();
    return request(`/ops/summary${query ? `?${query}` : ""}`, opsSummarySchema);
  },
  getOpsTimeline(options?: { hours?: number; limit?: number }) {
    const params = new URLSearchParams();
    if (options?.hours != null) {
      params.set("hours", String(options.hours));
    }
    if (options?.limit != null) {
      params.set("limit", String(options.limit));
    }
    const query = params.toString();
    return request(`/ops/timeline${query ? `?${query}` : ""}`, opsTimelineSchema);
  },
  getOpsFreshness(options?: { scope?: "core" | "all" }) {
    const params = new URLSearchParams();
    if (options?.scope) {
      params.set("scope", options.scope);
    }
    const query = params.toString();
    return request(`/ops/freshness${query ? `?${query}` : ""}`, opsFreshnessSchema);
  },
  getOpsSourceHealth(options?: { hours?: number }) {
    const params = new URLSearchParams();
    if (options?.hours != null) {
      params.set("hours", String(options.hours));
    }
    const query = params.toString();
    return request(`/ops/source-health${query ? `?${query}` : ""}`, opsSourceHealthSchema);
  },
  getOpsAlerts(options?: { limit?: number; status?: string }) {
    const params = new URLSearchParams();
    if (options?.limit != null) {
      params.set("limit", String(options.limit));
    }
    if (options?.status) {
      params.set("status", options.status);
    }
    const query = params.toString();
    return request(`/ops/alerts${query ? `?${query}` : ""}`, opsAlertsSchema);
  },
  ackOpsAlert(alertId: number) {
    return request(`/ops/alerts/${alertId}/ack`, z.object({ id: z.number(), status: z.string() }), {
      method: "POST",
      body: JSON.stringify({}),
    });
  },
  resolveOpsAlert(alertId: number) {
    return request(`/ops/alerts/${alertId}/resolve`, z.object({ id: z.number(), status: z.string() }), {
      method: "POST",
      body: JSON.stringify({}),
    });
  },
  getOpsDatasyncBootstrap() {
    return request("/ops/datasync/bootstrap", opsDatasyncBootstrapSchema);
  },
  listOpsDatasyncRuns(options?: { limit?: number; status?: string; taskType?: string }) {
    const params = new URLSearchParams();
    if (options?.limit != null) {
      params.set("limit", String(options.limit));
    }
    if (options?.status) {
      params.set("status", options.status);
    }
    if (options?.taskType) {
      params.set("task_type", options.taskType);
    }
    const query = params.toString();
    return request(`/ops/datasync/runs${query ? `?${query}` : ""}`, z.array(opsDatasyncRunSchema));
  },
  createOpsDatasyncRun(payload: {
    task_type: string;
    scope_type?: string;
    scope_json?: Record<string, unknown>;
    options_json?: Record<string, unknown>;
    batch_config_json?: Record<string, unknown>;
  }) {
    return request(
      "/ops/datasync/runs",
      z.object({
        run_id: z.number().optional(),
        status: z.string().optional(),
        preview: z.record(z.any()).optional(),
        summary_text: z.string().optional(),
      }),
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  },
  getOpsDatasyncRunLogs(runId: number, options?: { cursor?: number; limit?: number }) {
    const params = new URLSearchParams();
    if (options?.cursor != null) {
      params.set("cursor", String(options.cursor));
    }
    if (options?.limit != null) {
      params.set("limit", String(options.limit));
    }
    const query = params.toString();
    return request(`/ops/datasync/runs/${runId}/logs${query ? `?${query}` : ""}`, opsDatasyncRunLogsSchema);
  },
  getOpsDatasyncRun(runId: number) {
    return request(`/ops/datasync/runs/${runId}`, opsDatasyncRunSchema);
  },
  getOpsDatasyncRunSteps(runId: number) {
    return request(`/ops/datasync/runs/${runId}/steps`, z.array(opsDatasyncRunStepSchema));
  },
  listOpsDatasyncSchedules(options?: { limit?: number }) {
    const params = new URLSearchParams();
    if (options?.limit != null) {
      params.set("limit", String(options.limit));
    }
    const query = params.toString();
    return request(`/ops/datasync/schedules${query ? `?${query}` : ""}`, z.array(opsDatasyncScheduleSchema));
  },
  cancelOpsDatasyncRun(runId: number) {
    return request(`/ops/datasync/runs/${runId}/cancel`, z.any(), {
      method: "POST",
      body: JSON.stringify({}),
    });
  },
  retryOpsDatasyncRun(runId: number) {
    return request(`/ops/datasync/runs/${runId}/retry`, z.any(), {
      method: "POST",
      body: JSON.stringify({}),
    });
  },
  createOpsDatasyncSchedule(payload: Record<string, unknown>) {
    return request(
      `/ops/datasync/schedules`,
      z.object({
        schedule_id: z.number(),
      }),
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  },
  updateOpsDatasyncSchedule(scheduleId: number, payload: Record<string, unknown>) {
    return request(
      `/ops/datasync/schedules/${scheduleId}`,
      z.object({
        schedule_id: z.number(),
        status: z.string().optional(),
      }),
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );
  },
  pauseOpsDatasyncSchedule(scheduleId: number) {
    return request(`/ops/datasync/schedules/${scheduleId}/pause`, z.any(), {
      method: "POST",
      body: JSON.stringify({}),
    });
  },
  resumeOpsDatasyncSchedule(scheduleId: number) {
    return request(`/ops/datasync/schedules/${scheduleId}/resume`, z.any(), {
      method: "POST",
      body: JSON.stringify({}),
    });
  },
  deleteOpsDatasyncSchedule(scheduleId: number) {
    return request(`/ops/datasync/schedules/${scheduleId}`, z.any(), {
      method: "DELETE",
    });
  },
  getQuantPanelBootstrap() {
    return request("/quant/panel/bootstrap", quantPanelBootstrapSchema);
  },
  listQuantPanelRuns(options?: { limit?: number }) {
    const params = new URLSearchParams();
    if (options?.limit != null) {
      params.set("limit", String(options.limit));
    }
    const query = params.toString();
    return request(`/quant/panel/runs${query ? `?${query}` : ""}`, z.array(quantPanelRunSchema));
  },
  createQuantPanelRun(payload: Record<string, unknown>) {
    return request(
      "/quant/panel/runs",
      z.object({
        run_id: z.number(),
        status: z.string().optional(),
        params: z.record(z.any()).optional(),
      }),
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  },
  getQuantPanelRun(runId: number) {
    return request(`/quant/panel/runs/${runId}`, quantPanelRunDetailSchema);
  },
  getQuantPanelRunLogs(runId: number, options?: { cursor?: number; limit?: number }) {
    const params = new URLSearchParams();
    if (options?.cursor != null) {
      params.set("cursor", String(options.cursor));
    }
    if (options?.limit != null) {
      params.set("limit", String(options.limit));
    }
    const query = params.toString();
    return request(`/quant/panel/runs/${runId}/logs${query ? `?${query}` : ""}`, quantPanelRunLogsSchema);
  },
  getQuantPanelRunSelects(runId: number, options?: { limit?: number }) {
    const params = new URLSearchParams();
    if (options?.limit != null) {
      params.set("limit", String(options.limit));
    }
    const query = params.toString();
    return request(`/quant/panel/runs/${runId}/selects${query ? `?${query}` : ""}`, quantPanelRunSelectsSchema);
  },
  getQuantSelects(options?: { limit?: number; pickDate?: string }) {
    const params = new URLSearchParams();
    if (options?.limit != null) {
      params.set("limit", String(options.limit));
    }
    if (options?.pickDate) {
      params.set("pick_date", options.pickDate);
    }
    const query = params.toString();
    return request(`/quant/selects${query ? `?${query}` : ""}`, quantSelectsSchema);
  },
  getNewsFeed(options?: { category?: string; keyword?: string; tag?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.category) {
      params.set("category", options.category);
    }
    if (options?.keyword) {
      params.set("keyword", options.keyword);
    }
    if (options?.tag) {
      params.set("tag", options.tag);
    }
    if (options?.limit != null) {
      params.set("limit", String(options.limit));
    }
    if (options?.offset != null) {
      params.set("offset", String(options.offset));
    }
    const query = params.toString();
    return request(`/news/feed${query ? `?${query}` : ""}`, newsFeedSchema);
  },
  getNewsArticle(articlePath: string) {
    const normalized = String(articlePath || "").replace(/^\/+/, "");
    const encodedPath = normalized
      .split("/")
      .map((segment) => encodeURIComponent(segment))
      .join("/");
    return request(`/news/articles/${encodedPath}`, newsArticleSchema);
  },
  updateNewsMeta(payload: { article_path: string; category: string; tags?: string }) {
    return request("/news/meta", newsMetaUpdateSchema, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  authLogout() {
    return requestAbsolute("/auth/logout", z.object({ ok: z.boolean() }), {
      method: "POST",
    });
  },
  patchProfileMe(payload: { display_name?: string; settings?: Record<string, unknown> }) {
    return requestAbsolute("/api/profile/me", z.any(), {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
};
